/**
 * In-app QR scanner: camera (getUserMedia) + jsQR decoding over an
 * offscreen canvas, all LOCAL (no frame leaves the device). `onResult`
 * returns `true` if the scanned text was valid → it closes; if not, it keeps
 * scanning (e.g. someone else's QR in front of the camera).
 */

import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { X } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import jsQR from "jsqr";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

const SCAN_INTERVAL_MS = 250;

/** There is camera hardware/API with which to try scanning. */
export function canScanQr(): boolean {
  return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
}

export function QrScanner(props: { onResult: (text: string) => boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [cameraError, setCameraError] = createSignal(false);
  let video!: HTMLVideoElement;
  let stream: MediaStream | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
      } catch {
        setCameraError(true);
        return;
      }
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        /* the page was closed while the camera was starting */
      }
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });
      timer = setInterval(() => {
        if (!context || video.videoWidth === 0) {
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        const hit = jsQR(image.data, image.width, image.height);
        if (hit && props.onResult(hit.data)) {
          props.onClose();
        }
      }, SCAN_INTERVAL_MS);
    };
    void start();
  });

  onCleanup(() => {
    if (timer !== undefined) {
      clearInterval(timer);
    }
    for (const track of stream?.getTracks() ?? []) {
      track.stop();
    }
  });

  return (
    <div
      class="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/85 p-6"
      data-testid="qr-scanner"
    >
      <Show
        when={!cameraError()}
        fallback={
          <p class="text-sm font-medium text-white" data-testid="qr-scanner-error">
            {t(msg`Camera is not available or permission was denied.`)}
          </p>
        }
      >
        {/* El vídeo es solo visor local: nada sale del dispositivo. */}
        <video
          ref={video}
          class="max-h-[70vh] w-full max-w-md rounded-[var(--radius-2xl)] object-cover shadow-lg"
          muted
          playsinline
          data-testid="qr-scanner-video"
        />
        <p class="text-sm text-white/85">{t(msg`Point the camera at the QR on the sender.`)}</p>
      </Show>
      <Button
        variant="outline"
        data-testid="btn-scan-cancel"
        onClick={() => props.onClose()}
        class="gap-2 border-white/40 bg-transparent text-white hover:bg-white/10"
      >
        <X class="h-4 w-4" />
        {t(msg`Cancel`)}
      </Button>
    </div>
  );
}
